import React from 'react';
import { GitMerge } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { SettingsStatusChip } from './SettingsStatusChip';
import { SETTINGS_BTN_OUTLINE, SETTINGS_BTN_PRIMARY, SETTINGS_FIELD_LABEL, SETTINGS_TABLE_HEADER } from '../utils/settingsSectionStyles';

type ProjectProposal = { externalId: string; name: string; number: string; customerName: string; statusName: string; active: boolean };
type CustomerCandidate = { entityType: 'contact' | 'organization'; entityId: string; label: string; address: string };
type CustomerProposal = { externalId: string; name: string; zip: string; city: string; address: string; candidates: CustomerCandidate[] };

type ReconcileResult = {
  linkedProjects: number;
  newProjectLinks: number;
  linkedCustomers: number;
  newCustomerLinks: number;
  remoteProjects: number;
  remoteCustomers: number;
  projectProposals: ProjectProposal[];
  customerProposals: CustomerProposal[];
};

const PAGE = 25;

/**
 * Phase 0: links existing MeinSHK projects/customers to MP records before anything is pushed.
 * Exact matches are linked automatically; the two lists below need a decision per row.
 */
export function MeinshkReconcilePanel({
  canManage,
  lastReconcileAt,
  onDone,
}: {
  canManage: boolean;
  lastReconcileAt: string | null;
  onDone: () => void;
}) {
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState<ReconcileResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [choice, setChoice] = React.useState<Record<string, string>>({});
  const [projectPage, setProjectPage] = React.useState(1);
  const [customerPage, setCustomerPage] = React.useState(1);
  const [ignored, setIgnored] = React.useState<Set<string>>(new Set());

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/meinshk/reconcile', { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Bestandsabgleich fehlgeschlagen.');
      setResult(d.result as ReconcileResult);
      setProjectPage(1);
      setCustomerPage(1);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const linkCustomer = async (proposal: CustomerProposal) => {
    const picked = choice[proposal.externalId] ?? proposal.candidates[0]?.entityId;
    const cand = proposal.candidates.find((c) => c.entityId === picked);
    if (!cand) return;
    setBusyKey(`c:${proposal.externalId}`);
    setError(null);
    try {
      const res = await fetch('/api/settings/meinshk/links', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ externalId: proposal.externalId, entityType: cand.entityType, entityId: cand.entityId }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Zuordnung fehlgeschlagen.');
      setResult((prev) =>
        prev
          ? {
              ...prev,
              linkedCustomers: prev.linkedCustomers + 1,
              customerProposals: prev.customerProposals.filter((p) => p.externalId !== proposal.externalId),
            }
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
    }
  };

  const importProject = async (proposal: ProjectProposal) => {
    setBusyKey(`p:${proposal.externalId}`);
    setError(null);
    try {
      const res = await fetch('/api/settings/meinshk/import-construction', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ externalId: proposal.externalId }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Projekt konnte nicht angelegt werden.');
      setResult((prev) =>
        prev
          ? {
              ...prev,
              linkedProjects: prev.linkedProjects + 1,
              projectProposals: prev.projectProposals.filter((p) => p.externalId !== proposal.externalId),
            }
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
    }
  };

  const projects = (result?.projectProposals ?? []).filter((p) => !ignored.has(`p:${p.externalId}`));
  const customers = (result?.customerProposals ?? []).filter((p) => !ignored.has(`c:${p.externalId}`));
  const projectSlice = projects.slice(0, projectPage * PAGE);
  const customerSlice = customers.slice(0, customerPage * PAGE);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className={SETTINGS_FIELD_LABEL}>Bestandsabgleich</h3>
          <p className="text-xs text-app-text-subtle mt-0.5">
            Verknüpft vorhandene MeinSHK-Projekte (über die Projektnummer) und Kunden (über die Projekte) mit den Datensätzen hier, bevor etwas
            übertragen wird. {lastReconcileAt ? 'Zuletzt ausgeführt – kann jederzeit wiederholt werden.' : 'Noch nicht ausgeführt.'}
          </p>
        </div>
        {canManage ? (
          <button type="button" className={result ? SETTINGS_BTN_OUTLINE : SETTINGS_BTN_PRIMARY} disabled={running} onClick={() => void run()}>
            {running ? <LoadingSpinner size="sm" /> : <GitMerge className="w-3.5 h-3.5" />}
            {result ? 'Erneut abgleichen' : 'Bestand abgleichen'}
          </button>
        ) : null}
      </div>
      {error ? <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div> : null}

      {result ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-app-border p-3">
              <p className="text-xs font-medium text-app-text-subtle">Projekte verknüpft</p>
              <p className="mt-1 text-lg font-semibold text-app-text">
                {result.linkedProjects} <span className="text-sm font-normal text-app-text-subtle">von {result.remoteProjects}</span>
              </p>
              <p className="text-xs text-app-text-subtle">{result.newProjectLinks} neu in diesem Lauf</p>
            </div>
            <div className="rounded-md border border-app-border p-3">
              <p className="text-xs font-medium text-app-text-subtle">Kunden verknüpft</p>
              <p className="mt-1 text-lg font-semibold text-app-text">
                {result.linkedCustomers} <span className="text-sm font-normal text-app-text-subtle">von {result.remoteCustomers}</span>
              </p>
              <p className="text-xs text-app-text-subtle">{result.newCustomerLinks} neu in diesem Lauf</p>
            </div>
          </div>

          {projects.length ? (
            <div className="space-y-2">
              <p className="text-xs text-app-text-muted">
                <strong>{projects.length}</strong> aktive MeinSHK-Projekte haben hier kein Gegenstück. „Anlegen“ erstellt das Projekt (mit Kunde) hier und
                verknüpft es.
              </p>
              <div className="overflow-hidden rounded-md border border-app-border">
                <table className="w-full text-xs">
                  <thead className="bg-app-surface-muted text-left">
                    <tr>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Nr.</th>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Projekt</th>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Kunde</th>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Status</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {projectSlice.map((p) => (
                      <tr key={p.externalId} className="border-t border-app-border">
                        <td className="px-3 py-2 font-mono text-app-text-muted whitespace-nowrap">{p.number || '—'}</td>
                        <td className="px-3 py-2 text-app-text">{p.name || `Projekt ${p.externalId}`}</td>
                        <td className="px-3 py-2 text-app-text-muted">{p.customerName || '—'}</td>
                        <td className="px-3 py-2">
                          <SettingsStatusChip tone="muted">{p.statusName || 'ohne Status'}</SettingsStatusChip>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {canManage ? (
                            <>
                              <button
                                type="button"
                                className="text-xs font-medium text-pipedrive-blue hover:underline disabled:opacity-50"
                                disabled={busyKey === `p:${p.externalId}`}
                                onClick={() => void importProject(p)}
                              >
                                Anlegen
                              </button>
                              <button
                                type="button"
                                className="ml-3 text-xs text-app-text-subtle hover:text-app-text"
                                onClick={() => setIgnored((prev) => new Set(prev).add(`p:${p.externalId}`))}
                              >
                                Ignorieren
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {projectSlice.length < projects.length ? (
                <button type="button" className="text-xs font-medium text-pipedrive-blue hover:underline" onClick={() => setProjectPage((p) => p + 1)}>
                  Weitere {Math.min(PAGE, projects.length - projectSlice.length)} anzeigen
                </button>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-app-text-subtle">Alle aktiven MeinSHK-Projekte sind verknüpft.</p>
          )}

          {customers.length ? (
            <div className="space-y-2">
              <p className="text-xs text-app-text-muted">
                <strong>{customers.length}</strong> MeinSHK-Kunden ohne eindeutige Zuordnung. Vorschläge kommen aus Name und PLZ; ohne Zuordnung werden
                sie beim Push nicht angefasst.
              </p>
              <div className="overflow-hidden rounded-md border border-app-border">
                <table className="w-full text-xs">
                  <thead className="bg-app-surface-muted text-left">
                    <tr>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>MeinSHK-Kunde</th>
                      <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Zuordnen zu</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {customerSlice.map((c) => {
                      const selected = choice[c.externalId] ?? c.candidates[0]?.entityId ?? '';
                      return (
                        <tr key={c.externalId} className="border-t border-app-border align-top">
                          <td className="px-3 py-2">
                            <div className="font-medium text-app-text">{c.name || `Kunde ${c.externalId}`}</div>
                            <div className="text-app-text-subtle">{[c.address, [c.zip, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}</div>
                          </td>
                          <td className="px-3 py-2 min-w-[16rem]">
                            {c.candidates.length ? (
                              <AppSelect
                                size="sm"
                                value={selected}
                                onChange={(value) => setChoice((prev) => ({ ...prev, [c.externalId]: value }))}
                                options={c.candidates.map((cand) => ({
                                  value: cand.entityId,
                                  label: `${cand.label}${cand.address ? ` · ${cand.address.replace(/\s*\/\s*/g, ', ')}` : ''}`,
                                  badge: cand.entityType === 'organization' ? 'Firma' : undefined,
                                }))}
                                disabled={!canManage || busyKey === `c:${c.externalId}`}
                                aria-label={`Zuordnung für ${c.name}`}
                              />
                            ) : (
                              <span className="text-app-text-subtle">Kein passender Kontakt gefunden</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {canManage && c.candidates.length ? (
                              <button
                                type="button"
                                className="text-xs font-medium text-pipedrive-blue hover:underline disabled:opacity-50"
                                disabled={busyKey === `c:${c.externalId}`}
                                onClick={() => void linkCustomer(c)}
                              >
                                Zuordnen
                              </button>
                            ) : null}
                            {canManage ? (
                              <button
                                type="button"
                                className="ml-3 text-xs text-app-text-subtle hover:text-app-text"
                                onClick={() => setIgnored((prev) => new Set(prev).add(`c:${c.externalId}`))}
                              >
                                Ignorieren
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {customerSlice.length < customers.length ? (
                <button type="button" className="text-xs font-medium text-pipedrive-blue hover:underline" onClick={() => setCustomerPage((p) => p + 1)}>
                  Weitere {Math.min(PAGE, customers.length - customerSlice.length)} anzeigen
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
